import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface HomeSplitSliderPanelProps {
  title: string
  subtitle: string
  href: string
  linkLabel: string
  icon: LucideIcon
  accent?: 'primary' | 'sky'
  className?: string
  sliderMinHeight?: string
  children: ReactNode
}

export function HomeSplitSliderPanel({
  title,
  subtitle,
  href,
  linkLabel,
  icon: Icon,
  accent = 'primary',
  className,
  sliderMinHeight = 'min-h-[300px]',
  children,
}: HomeSplitSliderPanelProps) {
  const accentBar = accent === 'sky' ? 'bg-sky-500' : 'bg-primary-600'
  const iconBg = accent === 'sky' ? 'bg-sky-50 text-sky-700' : 'bg-primary-50 text-primary-700'

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className={cn('mb-1.5 h-1 w-10 rounded-full', accentBar)} aria-hidden />

      <div className="mb-1.5 flex min-h-[44px] items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span
            className={cn(
              'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
              iconBg,
            )}
          >
            <Icon className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-base font-bold leading-tight text-qbc-navy md:text-lg">
              {title}
            </h2>
            <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-neutral-500 md:text-xs">
              {subtitle}
            </p>
          </div>
        </div>

        <Link
          to={href}
          className="hidden shrink-0 items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-primary-700 shadow-sm transition-colors hover:border-primary-200 hover:bg-primary-50 sm:inline-flex"
        >
          {linkLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className={cn('relative flex flex-1 flex-col pb-0.5', sliderMinHeight)}>{children}</div>

      <Link
        to={href}
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 sm:hidden"
      >
        {linkLabel}
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}
